/** German Meta Pixel — the only pixel on this landing. */
export const META_PIXEL_ID = '1116836634611965'

const CONTENT_PARAMS = {
  content_name: 'Gospel Sharing Mega Bundle',
  content_type: 'product',
}

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  push?: unknown
  loaded?: boolean
  version?: string
  getState?: () => { pixels?: { id: string }[] }
}

declare global {
  interface Window {
    fbq?: FbqFunction
    _fbq?: FbqFunction
    __packPixelInstalled?: boolean
  }
}

const SCRIPT_ID = 'meta-pixel-script'
const NOSCRIPT_ID = 'meta-pixel-noscript'
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'

/**
 * True when the pixel was already initialised by the official base code pasted
 * into the static <head> of index.html (queued init before fbevents.js loads,
 * or registered pixel after it loads). Prevents a second init / PageView.
 */
function isPixelAlreadyInitialized(): boolean {
  const fbq = window.fbq
  if (!fbq) return false
  const queued = (fbq.queue ?? []).some((args) => args[0] === 'init' && String(args[1]) === META_PIXEL_ID)
  if (queued) return true
  try {
    const pixels = fbq.getState?.().pixels ?? []
    return pixels.some((p) => String(p.id) === META_PIXEL_ID)
  } catch {
    return false
  }
}

/**
 * Meta Pixel base code. The primary installation lives in the static <head> of
 * index.html (visible with Ctrl+U). This is a safe fallback: if the static code
 * already ran, it does nothing; otherwise it installs the pixel, runs init and
 * fires a single PageView. Guarded against duplicates.
 */
export function installMetaPixelBase(): void {
  if (typeof window === 'undefined') return
  if (window.__packPixelInstalled) return
  window.__packPixelInstalled = true

  if (isPixelAlreadyInitialized()) return

  if (!window.fbq) {
    const fbq: FbqFunction = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    window.fbq = fbq
    if (!window._fbq) window._fbq = fbq
  }

  if (!document.getElementById(SCRIPT_ID) && !document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = SCRIPT_SRC
    const firstScript = document.head.getElementsByTagName('script')[0]
    if (firstScript) document.head.insertBefore(script, firstScript)
    else document.head.appendChild(script)
  }

  if (!document.getElementById(NOSCRIPT_ID)) {
    const noscript = document.createElement('noscript')
    noscript.id = NOSCRIPT_ID
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1" />`
    document.head.appendChild(noscript)
  }

  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

let viewContentTracked = false

/** Fires ViewContent once per page load (PageView is handled by the base code). */
export function initMetaPixel(): void {
  if (typeof window === 'undefined') return
  installMetaPixelBase()
  if (viewContentTracked) return
  viewContentTracked = true
  window.fbq?.('track', 'ViewContent', CONTENT_PARAMS)
}

/** Fires InitiateCheckout without navigating (for new-tab clicks). */
export function trackInitiateCheckoutOnly(): void {
  window.fbq?.('track', 'InitiateCheckout', CONTENT_PARAMS)
}

let checkoutInFlight = false

/** Fires InitiateCheckout, then continues to checkout. A lock prevents double-click duplicates. */
export function trackInitiateCheckout(href: string): void {
  if (typeof window === 'undefined') return
  if (checkoutInFlight) return
  checkoutInFlight = true

  window.fbq?.('track', 'InitiateCheckout', CONTENT_PARAMS)

  // Short delay so the pixel request leaves the browser before navigation.
  window.setTimeout(() => {
    window.location.href = href
    window.setTimeout(() => {
      checkoutInFlight = false
    }, 1500)
  }, 300)
}

// If the visitor comes back from the checkout via the browser's back button
// (page restored from cache), unlock so the next click is tracked again.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) checkoutInFlight = false
  })
}
